import { Relation, Person } from './contact';
import { ApprovalMeetingStatus } from 'constant/approval';
import type { MeetingOrderFn, MeetingOrderForm, MeetingOrderParams } from 'nicehubs-plus';

/** 预约表单 */
export type IMeetingOrderForm = MeetingOrderForm
/** 预约函数 */
export type IMeetingOrderFn = MeetingOrderFn
/** 预约、编辑参数 */
export type OrderMeetingV2 = MeetingOrderParams

// 会议修改记录
export interface MeetingChange {
  createTime: number;
  // 变更属性的key
  key: string;
  // 修改者的userId
  modifier: string;
  newValue: string;
  oldValue: string;
}

export enum PublicStatus {
  NO = 0,
  YES = 1
}

// 私密会议类型
export enum MeetingType {
  PRIVATE = 1,
  PUBLIC = 2
}

// 成员签到
export enum MemberSignStatus {
  // 初始值，未开始签到
  NO_BEGIN_SIGN = 1,

  // 开始签到
  SIGNNING = 2,

  // 结束签到
  CLOSE_SIGN = 3
}

// 会议室签到状态
export enum OrderSignStatus {
  NO_BEGIN_SIGN = 1,
  FINISH_SIGN = 2
}

// 会议室签到状态
export enum NewOrderSignStatus {
  NO_BEGIN_SIGN = 0,
  FINISH_SIGN = 1
}

// 在会议前多?分钟发送提醒
export enum NoticeType {
  ON_TIME = 'on_time',
  BEFORE_5 = 'before_5',
  BEFORE_10 = 'before_10',
  BEFORE_15 = 'before_15',
  BEFORE_20 = 'before_20',
  BEFORE_30 = 'before_30'
}

export enum OrderMeetingOrigin {
  MAXHUB_MEETING_OA_WEB = 'MAXHUB_MEETING_OA_WEB',
  MAXHUB_MEETING_WEB = 'MAXHUB_MEETING_WEB'
}

export interface RepeatMeetingDetails {
  repeatMeetingNo: string;
  repeatStartDate: number;
  repeatEndDate: number;
  userId: string;
}

// 参会成员
export interface MeetingMember {
  createTime: number;
  from: 0;
  invitor: Person;
  // 会议ID
  meeting: string;
  relationship: {
    relation: Relation;
    commonCompany: boolean;
  };
  remark: string | null;
  repeatMeeting: null;
  state: AttendState;
  updateTime: number;
  user: Person;
  _id: string;
}

// 会议进度状态
export enum MeetingState {
  // 待开始
  READY = 0,
  // 进行中
  BEGIN = 1,
  // 已解散
  DISBAND = 2,
  // 已结束
  END = 3,
  // 已归档
  SAVED = 4
}

export enum AttendState {
  // 新邀请
  TO_BE_CONFIRMED = 0,
  // 已拒绝
  REFUSED = 1,
  // 已确认
  JOINED = 2,
  // 已请假
  ASK_FOR_LEAVE = 3,
  // 会议撤回
  INVITE_WITHDRAW = 4
}

export enum OrderState {
  // 会议前
  BEFORE = 1,
  // 会议中
  MEETING = 2,
  // 会议后
  AFTER = 3,
  // 已取消
  CANCEL = 4
}
// iot设备状态
export interface MeetingIotMsg {
  roomId: string;
  deviceName: string;
  deviceStatus: number;
}

export interface Meeting {
  _id: string;
  meetingNo: string;
  address: string;
  beginTime: number;
  changeLogs: MeetingChange[];
  content: string;
  createTime: number;
  creator: string;
  creatorStaffId: string;
  endTime: number;
  isPublic: PublicStatus;
  isBeInvited: boolean;
  master: string;
  // Todo
  meetingType: number;
  memberSignStatus: MemberSignStatus;
  members: MeetingMember[];
  noticeLabels: NoticeType[];
  // 会议室预约单号
  order: string;
  orderDescription: string | null;
  orderSignStatus: number | null;
  orderState: OrderState;
  // 已提醒次数
  remindNum: number;
  isRepeatMeeting:boolean;
  repeatMeetingNo: string;
  repeatMeeting: string | null;
  repeatMeetingDetails: RepeatMeetingDetails | null;
  repeatMeetingType: number | null;
  // 会议室ID
  roomId: string;
  state: MeetingState;
  subject: string;
  // ?云盘授权码
  accessKey: string;
  // ?云盘分享Id
  shareId: string;
  isMyself: IsMyself;
  userName: string;
  // 会议类型
  scope: number;
  // iot信息
  iotMessage: MeetingIotMsg[];
  meetingSignView?: {
    // 会议室签到状态 未开始签到:1，完成签到:2
    signStatus: number;
    // 是否可以签到
    isCanSign: boolean;
  };
  status: AllMeetingStatus;
  roomOrderList?: IRoomList[];
  meetingAuditRecordView?: {
    id: string;
    status: ApprovalMeetingStatus;
    reason: string;
    auditorList: Person[];
    meetingNo: string;
  };
}

export interface IRoomList {
  id: string;
  name: string;
  companyId: string;
  address: string;
  orderNo: string
}

export enum IsMyself {
  YES = 0,
  NO = 1
}

export enum SummaryState {
  UNREAD = 0,
  READ = 1
}

// 文件上传接口结果
export interface MeetFileUploadRes {
  id: string;
  name: string;
  size: number;
  contentType: string;
  downloadUrl?: string;
  createdDate?: number;
  isTransient?: boolean;
}

// 上传会议文件
export interface MeetingFile {
  _id: string;
  itemId: string;
  status: 'wait' | 'loading' | 'success' | 'fail';
  name: string;
  contentType: string;
  size: number;
  percent: number;
  formData: FormData | null;
  url: string;
  createdDate: number;
}

export enum MeetingConflict {
  NO = 0,
  YES = 1
}

export interface MeetingRecord {
  changeLogs: MeetingChange[];
  conflict: MeetingConflict;
  inviteState: AttendState;
  noteContent: null;
  noteSubject: string;
  summaryState: SummaryState;
  user: string;
  userReadTime: number;
  meeting: Meeting;
}

// 预约信息中的会议成员
export interface OrderMembers {
  ids: string[];
  fromRecommended: string[];
}

// 预约信息 & 修改信息
export interface OrderMeeting {
  subject: string;
  beginTime: number;
  endTime: number;
  attachments?: string[];
  roomId?: string;
  scope: number;
  isPublic: number;
  repeatMeetingType?: number | null;
  noticeLabels?: NoticeType[];
  members?: OrderMembers;
  address?: string;
  content?: string;
  useRemote?: number;
  extend?: any;
  origin?: OrderMeetingOrigin | string;
}

// 审批的预约信息
export interface ApprovalOrderMeeting {
  reason: string;
  meetingCreateForm: OrderMeeting;
  auditorStaffIdList: string[];
}

export interface MeetingSocket {
  address: string;
  content: string;
  endTime: number;
  meetingNo: string;
  operator: {
    staffId: string;
    userId: string;
  };
  roomId: string;
  startTime: number;
  theme: string;
}

export interface MeetingSummary {
  content: string;
  publishedTime: number;
  state: number;
  subject: string;
  todos: string[];
  _id: string;
}

export interface HelpMsg {
  content: string;
}

export enum InventTag {
  join,
  leave,
  reject
}

export interface IMeetingCalendarDetailList {
  date: string;
  scheduleList: IScheduleList[];
}

export interface IScheduleList {
  scheduleId: string;
  beginTime: string;
  endTime: string;
  subject: string;
  organizerName: string;
  address: string;
  meetingStatus: AllMeetingStatus;
  meetingNo: string;
}

export enum AllMeetingStatus {
  /**
   * 审计中
   */
  AUDITING = 'AUDITING',

  /**
   * 审核拒绝
   */
  AUDIT_REFUSE = 'AUDIT_REFUSE',

  /**
   * 审核撤销
   */
  AUDIT_REVOKE = 'AUDIT_REVOKE',
  /**
   * 审核过期
   */
  AUDIT_EXPIRED = 'AUDIT_EXPIRED',

  /**
   * 会议前
   */
  BEFORE = 'BEFORE',

  /**
   * 会议中
   */
  DURING = 'DURING',

  /**
   * 会议结束
   */
  FINISH = 'FINISH',

  /**
   * 会议取消
   */
  CANCEL = 'CANCEL',

  /**
   * 已归档
   */
  FILED = 'FILED'
}

/** 线上会议配置 */
export interface OnlineMeetingConfig {
  /** 线上平台类型 */
  meetingType: number
  /** 平台名称 */
  name: string
  /** 是否默认选中 */
  isDefault: boolean
  /** 该选项是否可选 */
  isSelect: boolean
}

/** 预约设置 */
export interface OrderSettings {
    // 是否公开通讯录
    publicAddressBook?: {
      value?: boolean
    };
    /** 会议详情超链接（TODO: 迁移至会议模板配置） */
    linkList: { title: string; url: string }[]
}

/** 预约设置 */
export interface IRelations {
  // 关系. 1:互为联系人, 2:单向添加，3:未添加
  relation:1|2|3
}

export interface IMeetingFile {
  id?:string;
  fileKey:string;
  fileName:string;
  contentType:string;
  size:number;
}
